/* Simple geometric bakery motifs + animated decoration layers.
   Ported from the Claude Design handoff (motifs.jsx). currentColor-driven. */
import { useMemo, type CSSProperties, type ReactNode } from "react";
// Inlined as a base64 data URI (bundled, not a network asset) so the brand
// wordmark repaints instantly when the app shell remounts between tabs — a
// fresh <img> with a fetched src would flash; an inline one never does.
import gailsLogoUrl from "../assets/gails-logo.png?inline";

type SvgProps = { size?: number; color?: string; style?: CSSProperties };

export function Crescent({ size = 22, color = "currentColor", style }: SvgProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={style}>
      <path d="M16.5 3.2a9 9 0 1 0 4.3 7.3 6.6 6.6 0 1 1-4.3-7.3Z" fill={color} />
    </svg>
  );
}

export function CoffeeCup({ size = 26, color = "currentColor", style }: SvgProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 26 26"
      fill="none"
      stroke={color}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={style}
    >
      <path d="M5 9.5h12v6a4 4 0 0 1-4 4H9a4 4 0 0 1-4-4v-6Z" />
      <path d="M17 11h2.4a2.4 2.4 0 0 1 0 4.8H17" />
      <path d="M4 23h14" />
    </svg>
  );
}

export function OvenDial({
  size = 26,
  color = "currentColor",
  spin = false,
  style,
}: SvgProps & { spin?: boolean }) {
  const ticks = Array.from({ length: 12 });
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" fill="none" style={style}>
      <circle cx="20" cy="20" r="16" stroke={color} strokeWidth="2.4" opacity=".25" />
      {ticks.map((_, i) => {
        const a = (i / 12) * Math.PI * 2;
        const r1 = 16;
        const r2 = i % 3 === 0 ? 12 : 13.6;
        return (
          <line
            key={i}
            stroke={color}
            strokeWidth={i % 3 === 0 ? 2 : 1.2}
            strokeLinecap="round"
            x1={20 + Math.sin(a) * r1}
            y1={20 - Math.cos(a) * r1}
            x2={20 + Math.sin(a) * r2}
            y2={20 - Math.cos(a) * r2}
          />
        );
      })}
      <g style={spin ? { transformOrigin: "20px 20px", animation: "bk-spin 8s linear infinite" } : undefined}>
        <line x1="20" y1="20" x2="20" y2="9" stroke={color} strokeWidth="2.6" strokeLinecap="round" />
      </g>
      <circle cx="20" cy="20" r="2.6" fill={color} />
    </svg>
  );
}

export function Spark({ size = 18, color = "currentColor", style }: SvgProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} style={style}>
      <path d="M12 0c.7 6 2.8 9.3 12 12-9.2 2.7-11.3 6-12 12-.7-6-2.8-9.3-12-12C9.2 9.3 11.3 6 12 0Z" />
    </svg>
  );
}

export function OptGlyph({ kind, size = 22 }: { kind: string; size?: number }) {
  const c = "#fff";
  if (kind === "a")
    return (
      <svg width={size} height={size} viewBox="0 0 24 24">
        <path d="M12 3 22 21 2 21Z" fill={c} />
      </svg>
    );
  if (kind === "b")
    return (
      <svg width={size} height={size} viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="9.5" fill="#3a2a08" />
      </svg>
    );
  if (kind === "c")
    return (
      <svg width={size} height={size} viewBox="0 0 24 24">
        <rect x="3.5" y="3.5" width="17" height="17" rx="3.5" fill={c} />
      </svg>
    );
  return (
    <svg width={size} height={size} viewBox="0 0 24 24">
      <path d="M12 2 22 12 12 22 2 12Z" fill={c} />
    </svg>
  );
}

export function Steam({
  count = 3,
  color,
  height = 26,
  style,
}: {
  count?: number;
  color?: string;
  height?: number;
  style?: CSSProperties;
}) {
  return (
    <div className="bk-steam-wrap" style={{ display: "flex", gap: 7, alignItems: "flex-end", ...style }}>
      {Array.from({ length: count }).map((_, i) => (
        <span
          key={i}
          className="bk-steam"
          style={{
            position: "relative",
            bottom: 0,
            height,
            background: `linear-gradient(${color || "var(--crust-soft)"}, transparent)`,
            animationDelay: `${i * 0.5}s`,
          }}
        />
      ))}
    </div>
  );
}

export function Confetti({
  count = 40,
  colors = ["#B01E24", "#D89A36", "#6E8C45", "#8A4A63", "#FFFCF6"],
}: {
  count?: number;
  colors?: string[];
}) {
  const pieces = useMemo(
    () =>
      Array.from({ length: count }).map(() => ({
        left: Math.random() * 100,
        bg: colors[(Math.random() * colors.length) | 0],
        dur: 2.6 + Math.random() * 2.4,
        delay: Math.random() * 3,
        round: Math.random() > 0.6,
        w: 7 + Math.random() * 6,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [count],
  );
  return (
    <div className="bk-confetti-layer">
      {pieces.map((p, i) => (
        <i
          key={i}
          style={{
            left: `${p.left}%`,
            background: p.bg,
            width: p.w,
            height: p.round ? p.w : p.w * 1.5,
            borderRadius: p.round ? "50%" : 2,
            animationDuration: `${p.dur}s`,
            animationDelay: `${p.delay}s`,
          }}
        />
      ))}
    </div>
  );
}

export function FlourDust({ count = 22 }: { count?: number }) {
  const bits = useMemo(
    () =>
      Array.from({ length: count }).map(() => ({
        left: Math.random() * 100,
        top: Math.random() * 40,
        dur: 3 + Math.random() * 3,
        delay: Math.random() * 4,
        dx: Math.random() * 20 - 10 + "px",
        s: 0.6 + Math.random() * 1.4,
      })),
    [count],
  );
  return (
    <div className="bk-dust-layer">
      {bits.map((b, i) => (
        <i
          key={i}
          style={
            {
              left: `${b.left}%`,
              top: `${b.top}%`,
              "--dx": b.dx,
              transform: `scale(${b.s})`,
              animationDuration: `${b.dur}s`,
              animationDelay: `${b.delay}s`,
            } as CSSProperties
          }
        />
      ))}
    </div>
  );
}

/* GAIL's wordmark image. Transparent red PNG (401×92). `light` inverts it to
   white for use on dark / coloured panels. */
export function GailsLogo({
  h = 19,
  light = false,
  style,
}: {
  h?: number;
  light?: boolean;
  style?: CSSProperties;
}) {
  // Explicit width (from the 401×92 source) + sync decode so a shell remount
  // repaints the cached logo instantly instead of reflowing/flashing.
  const w = Math.round((h * 401) / 92);
  return (
    <img
      src={gailsLogoUrl}
      alt="GAIL's"
      width={w}
      height={h}
      decoding="sync"
      style={{
        height: h,
        width: w,
        display: "block",
        filter: light ? "brightness(0) invert(1)" : undefined,
        ...style,
      }}
    />
  );
}

/* Brand lockup: GAIL's wordmark · hairline divider · italic "BakeOff".
   Falls back to bk-* tokens when the Atelier vars aren't in scope (play screens). */
export function Logo({ scale = 1, light = false }: { scale?: number; light?: boolean }) {
  // Scale intrinsically (real width grows) rather than via transform, so the logo
  // reserves layout space and centred parents can't let it overlap its neighbours.
  const h = Math.round(19 * scale);
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: Math.round(11 * scale),
      }}
    >
      <GailsLogo h={h} light={light} />
      <span
        style={{
          width: Math.max(1, Math.round(scale)),
          height: Math.round(18 * scale),
          background: light ? "rgba(255,255,255,.45)" : "var(--atl-line, var(--line, #E1D7C7))",
        }}
      />
      <span
        style={{
          fontFamily: '"Sprig", Georgia, serif',
          fontWeight: 500,
          fontStyle: "italic",
          fontSize: Math.round(19 * scale),
          color: light ? "#fff" : "var(--atl-accent, var(--red, #8C2F3D))",
          lineHeight: 1,
        }}
      >
        BakeOff
      </span>
    </div>
  );
}

export function Placeholder({
  label = "image",
  h = 120,
  style,
  radius = 14,
}: {
  label?: string;
  h?: number;
  style?: CSSProperties;
  radius?: number;
}): ReactNode {
  return (
    <div
      style={{
        height: h,
        borderRadius: radius,
        display: "grid",
        placeItems: "center",
        color: "var(--ink-faint)",
        fontFamily: "var(--mono)",
        fontSize: 11,
        letterSpacing: ".08em",
        textTransform: "uppercase",
        border: "1.5px dashed var(--line)",
        background: "repeating-linear-gradient(135deg, var(--inset) 0 11px, transparent 11px 22px)",
        ...style,
      }}
    >
      {label}
    </div>
  );
}
